import React, { createContext, useContext, useEffect, useRef } from 'react'

type ScrollContextValues = {
  subscribe: (callback: () => void) => () => void
}

const ScrollContext = createContext<ScrollContextValues | null>(null)

export function ScrollProvider({
  children,
  containerRef,
}: {
  children: React.ReactNode
  containerRef: React.RefObject<HTMLDivElement | null>
}) {
  const subscribers = useRef<Set<() => void>>(new Set())
  const ticking = useRef(false)

  const subscribe = (callback: () => void) => {
    subscribers.current.add(callback)
    return () => {
      subscribers.current.delete(callback)
    }
  }

  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current

    const onScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(() => {
          subscribers.current.forEach((callback) => callback())
          ticking.current = false
        })
        ticking.current = true
      }
    }

    container.addEventListener('scroll', onScroll, { passive: true })
    // container.addEventListener('touchend', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)

    return () => {
      container.removeEventListener('scroll', onScroll)
      // container.removeEventListener('touchend', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [containerRef])

  return (
    <ScrollContext.Provider value={{ subscribe }}>
      {children}
    </ScrollContext.Provider>
  )
}

export function useScroll() {
  const context = useContext(ScrollContext)
  if (!context) {
    throw new Error('useScroll must be used within ScrollProvider')
  }
  return context
}
